//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Tab } from '@mui/material';
import { Tabs, TabPanel } from '@/components/_common/Tabs';
import { selectJob, selectJobIsLoading } from '@/redux/selectors/jobSelectors';
import {
  setActiveTitle as setActiveTitleIdV1,
  getTitleDetails as getTitleDetailsV1,
  getTitleVersions as getTitleVersionsV1
} from '@/redux/slices/titleSlice';
import { setActiveTitleId as setActiveTitleIdV2, fetchTitleV2, getTitleVersions as getTitleVersionsV2 } from '@/redux/slices/titleV2Slice';
import { TitleEntity } from '@/redux/slices/slice_types';
import TabBadge from '@/components/_common/TabBadge';
import { selectDataTable } from '@/redux/selectors/dataTableSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import JobTabFiles from './JobTabFiles';
import { JobTabPagination } from '../Job';
import JobTitleCard from './JobTitleCard/JobTitleCard';
import JobMetadata from './JobMetadata/JobMetadata';
import JobTabTitleOverview from './JobTabTitleOverview';
import JobTabTitleOverviewV2 from './JobTabTitleOverviewV2';
import JobTabRelatedTasks from './JobTabRelatedTasks';
import JobTabAssetStatus from './JobTabAssetStatus/JobTabAssetStatus';
import { ConfigContext } from '@/contextAPI/configContext';
import JobTabFullInputs from './JobTabFullInputs';
import JobTabBundleJobs from './JobTabBundleJobs.tsx';

// the tabs that appear on the Job Detail page, rendered by JobDetail.jsx
// the tabs are:
//    Assets: JobTabAssetStatus
//    Additional Info: JobMetadata
//    Related Tasks: JobTabRelatedTasks
//    Title Overview: JobTabTitleOverview
//    Related Files: JobTabFiles
//    Full Inputs: JobTabFullInputs (optional, based on config)
//    Bundle Jobs: JobTabBundleJobs (optional, based on if this job has a bundle id)

type JobTabsProps = {
  tab: number;
  handleTabChange: (tab: number) => void;
  showAllFiles: boolean;
  toggleShowAllFiles: () => void;
  pagination: JobTabPagination;
  updatePagination: (val: JobTabPagination) => void;
};

const isTitle = (possiblyTitle: any): possiblyTitle is TitleEntity => {
  return possiblyTitle.id !== undefined;
};

const JobTabs = ({ tab, handleTabChange, showAllFiles, toggleShowAllFiles, pagination, updatePagination }: JobTabsProps) => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const showFullInputs = useContext(ConfigContext)?.jobDetails?.showFullInputs;
  const loading = useAppSelector(selectJobIsLoading);
  const totalRelatedTasks = useAppSelector(selectDataTable).jobDetailTasks?.pagination?.total || 0;
  // const jobHid = useAppSelector(selectJobHid);
  const dispatch = useAppDispatch();
  const { title = {}, additional_info, bundle_job_hid, job_type } = useAppSelector(selectJob);
  const [isTitleSet, setIsTitleSet] = useState(false);
  const hasBundle = useMemo(() => job_type === 'bundle' || bundle_job_hid, [job_type, bundle_job_hid]);

  useEffect(() => {
    if (isTitleSet || !isTitle(title)) return;
    let titleRequest;
    let versionsRequest;
    // Fetching title details and versions required for TitleDetailMetadata and TitleDetailSynopsis that used deeper down component tree
    if (isTitleV2) {
      dispatch(setActiveTitleIdV2(title.id));
      titleRequest = dispatch(getTitleVersionsV2(title.id));
      versionsRequest = dispatch(fetchTitleV2(title.id));
    } else {
      dispatch(setActiveTitleIdV1(title.id));
      titleRequest = dispatch(getTitleVersionsV1(title.id));
      versionsRequest = dispatch(getTitleDetailsV1(title.id));
    }
    setIsTitleSet(true);
    return () => {
      titleRequest.abort();
      versionsRequest.abort();
    };
  }, [title]);

  // KYLE: dont need this anymore because we reload the tasks tab when it activates
  // useEffect(() => {
  //   if (jobHid) {
  //     dispatch(setCurrentTable('jobDetailTasks'));
  //   }
  // }, [jobHid]);

  return (
    <div>
      <JobTitleCard />
      {!loading && (
        <>
          <StyledTabs value={tab} onChange={(_e, val) => handleTabChange(val)} aria-label="job detail tabs">
            <Tab label="Assets" />
            <Tab label="Additional Info" />
            <Tab label={<TabBadge label="Related Tasks" count={totalRelatedTasks} />} />
            <Tab label="Title Overview" />
            <Tab label="Related Files" />
            {showFullInputs && <Tab label="Full Inputs" />}
            {hasBundle && <Tab label="Bundle Jobs" />}
          </StyledTabs>

          <TabPanel value={tab} index={0}>
            <JobTabAssetStatus />
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <JobMetadata schema={additional_info} />
          </TabPanel>

          <TabPanel value={tab} index={2}>
            {/* By doing this conditional we force the table to-render on click which allows multiple tables on one page */}
            {tab === 2 && <JobTabRelatedTasks />}
          </TabPanel>

          <TabPanel value={tab} index={3}>
            {isTitleV2 ? <JobTabTitleOverviewV2 /> : <JobTabTitleOverview />}
          </TabPanel>

          <TabPanel value={tab} index={4}>
            <JobTabFiles
              showAllFiles={showAllFiles}
              toggleShowAllFiles={toggleShowAllFiles}
              pagination={pagination}
              updatePagination={updatePagination}
            />
          </TabPanel>
          {showFullInputs && (
            <TabPanel value={tab} index={5}>
              <JobTabFullInputs />
            </TabPanel>
          )}
          {hasBundle && (
            <TabPanel value={tab} index={showFullInputs ? 6 : 5}>
              {/* By doing this conditional we force the table to-render on click which allows multiple tables on one page */}
              {tab === (showFullInputs ? 6 : 5) && <JobTabBundleJobs />}
            </TabPanel>
          )}
        </>
      )}
    </div>
  );
};

export default JobTabs;

const StyledTabs = styled(Tabs)`
  & .MuiTab-root.MuiTab-root:not(.Mui-selected) {
    background: none;
  }
`;
